import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FloppyDisk, DiscordLogo, ArrowSquareOut, ArrowsClockwise, Robot, ShoppingCart, GameController } from "@phosphor-icons/react";
import api from "@/lib/api";

function Row({ label, ok, hint }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-white/5">
      <div>
        <div className="font-mono text-sm">{label}</div>
        <div className="kbd-label text-white/40">{hint}</div>
      </div>
      <div className={`kbd-label px-3 py-1 border ${ok ? "border-emerald-500 text-emerald-400" : "border-white/20 text-white/50"}`}>
        {ok ? "CONFIGURED" : "NOT SET"}
      </div>
    </div>
  );
}

export default function SettingsTab() {
  const [invite, setInvite] = useState("");
  const [feePct, setFeePct] = useState("0");
  const [savingFee, setSavingFee] = useState(false);
  const [guildId, setGuildId] = useState("");
  const [channelId, setChannelId] = useState("");
  const [savingServer, setSavingServer] = useState(false);
  const [health, setHealth] = useState({});
  const [botStatus, setBotStatus] = useState(null);
  const [tebexStatus, setTebexStatus] = useState(null);
  const [finiStatus, setFiniStatus] = useState(null);
  const [tebexSyncing, setTebexSyncing] = useState(false);
  const [finiSyncing, setFiniSyncing] = useState(false);
  const [verifyTxId, setVerifyTxId] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  async function load() {
    const [s, h] = await Promise.all([api.get("/settings"), api.get("/health")]);
    setInvite(s.data.discord_invite_url || "");
    setFeePct(String(s.data.tebex_fee_pct ?? "0"));
    setGuildId(s.data.discord_guild_id || "");
    setChannelId(s.data.discord_invite_channel_id || "");
    setHealth(h.data);
    try {
      const b = await api.get("/discord/status");
      setBotStatus(b.data);
    } catch (e) { setBotStatus(null); }
    try {
      const t = await api.get("/tebex/status");
      setTebexStatus(t.data);
    } catch (e) { setTebexStatus(null); }
    try {
      const f = await api.get("/fivem/fini/status");
      setFiniStatus(f.data);
    } catch (e) { setFiniStatus(null); }
  }
  useEffect(() => { load(); }, []);

  async function syncTebex() {
    // Legacy — kept for backward compat but the Checkout API doesn't support polling.
    setTebexSyncing(true);
    try {
      const r = await api.post("/tebex/verify", { transaction_id: verifyTxId });
      if (r.data.ok === false) {
        toast.error(`Tebex verify failed: ${r.data.error}`);
      } else {
        toast.success(`Attributed ${r.data.transaction_id} → creator ${r.data.creator_id || "unattributed"}`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Tebex verify failed");
    } finally { setTebexSyncing(false); }
  }

  async function verifyTx() {
    const tx = verifyTxId.trim();
    if (!tx) return toast.error("Paste a tbx-XXX transaction id");
    setVerifying(true);
    try {
      const r = await api.post("/tebex/verify", { transaction_id: tx });
      if (r.data.ok === false) {
        toast.error(r.data.error);
      } else {
        toast.success(`Attributed ${tx} → ${r.data.creator_id ? "creator " + r.data.creator_id : "no creator (unattributed)"}`);
        setVerifyTxId("");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setVerifying(false); }
  }

  async function syncFini() {
    setFiniSyncing(true);
    try {
      const r = await api.post("/fivem/fini/sync");
      if (r.data.ok === false) {
        toast.error(`Fini sync failed: ${r.data.hint || r.data.error}`);
      } else {
        toast.success(`Fini Apollo: ${r.data.online} online · ${r.data.matched} attributed · ${r.data.new_seen} new`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fini sync failed");
    } finally { setFiniSyncing(false); }
  }

  async function saveServer() {
    if (!/^\d{5,}$/.test(guildId.trim())) return toast.error("Enter a valid numeric Server (Guild) ID");
    setSavingServer(true);
    try {
      await api.put("/settings/discord_guild_id", { value: guildId.trim() });
      await api.put("/settings/discord_invite_channel_id", { value: channelId.trim() });
      toast.info("Reconnecting bot to the new server…");
      const r = await api.post("/discord/reconnect");
      if (r.data.ok) toast.success(`Bot connected to guild ${r.data.guild_id}`);
      else toast.error(r.data.reason || "Bot did not connect — check the bot is in that server with Manage Server");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSavingServer(false); }
  }

  async function saveFee() {
    setSavingFee(true);
    try {
      await api.put("/settings/tebex_fee_pct", { value: String(parseFloat(feePct) || 0) });
      toast.success("Tebex fee % saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSavingFee(false); }
  }

  async function save() {
    setSaving(true);
    try {
      await api.put("/settings/discord_invite_url", { value: invite.trim() });
      toast.success("Discord invite URL saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  }

  async function refreshInvites() {
    setRefreshing(true);
    try {
      const r = await api.post("/discord/refresh-invites");
      if (r.data.ok) toast.success(`Cached ${r.data.invite_count} invites`);
      else toast.error(r.data.reason || "Refresh failed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setRefreshing(false); }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="display-heading font-black text-3xl">Settings</h2>
        <div className="kbd-label text-white/40 mt-1">TRACKED SERVER · DISCORD INVITE · BOT · INTEGRATION STATUS</div>
      </div>

      <section className="card-tactical p-6 border-l-4 border-l-neon-cyan">
        <div className="flex items-center gap-2 mb-2">
          <Robot size={22} color="#00F0FF" weight="fill" />
          <h3 className="display-heading font-bold text-lg">TRACKED DISCORD SERVER</h3>
        </div>
        <p className="text-white/60 text-sm mb-4">
          The single Discord server this tool tracks. Paste your <span className="text-neon-cyan">main server's Guild ID</span> and the
          <span className="text-neon-cyan"> channel</span> where generated invites should point, then Save & Reconnect.
          The bot must already be in that server with <span className="text-neon-yellow">Manage Server</span> + <span className="text-neon-yellow">Create Instant Invite</span> permissions.
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <div className="kbd-label mb-1">SERVER (GUILD) ID</div>
            <input value={guildId} onChange={(e) => setGuildId(e.target.value)} placeholder="e.g. 1528089565812756722" data-testid="settings-guild-input"
              className="w-full bg-black/60 border border-white/10 px-3 py-3 font-mono text-sm focus:border-neon-cyan outline-none" />
          </div>
          <div>
            <div className="kbd-label mb-1">INVITE CHANNEL ID</div>
            <input value={channelId} onChange={(e) => setChannelId(e.target.value)} placeholder="e.g. 1528089566282780754" data-testid="settings-channel-input"
              className="w-full bg-black/60 border border-white/10 px-3 py-3 font-mono text-sm focus:border-neon-cyan outline-none" />
          </div>
        </div>
        <div className="mt-4 flex items-center gap-4 flex-wrap">
          <button onClick={saveServer} disabled={savingServer} data-testid="settings-server-save" className="neon-btn px-5 py-3 inline-flex items-center gap-2 disabled:opacity-50">
            <ArrowsClockwise size={16} className={savingServer ? "animate-spin" : ""} /> {savingServer ? "RECONNECTING…" : "SAVE & RECONNECT BOT"}
          </button>
          {botStatus && (
            <div className="kbd-label font-mono text-xs">
              {botStatus.ready
                ? <span className="text-emerald-400">● CONNECTED · GUILD {botStatus.guild_id}</span>
                : <span className="text-red-400">● NOT CONNECTED{botStatus.last_error ? ` · ${botStatus.last_error}` : ""}</span>}
            </div>
          )}
        </div>
        <div className="mt-2 text-[11px] text-white/40 font-mono">
          Tip: Discord → Settings → Advanced → Developer Mode, then right-click the server / channel → Copy ID.
        </div>
      </section>

      <section className="card-tactical p-6">
        <div className="flex items-center gap-2 mb-4">
          <DiscordLogo size={22} color="#00F0FF" weight="fill" />
          <h3 className="display-heading font-bold text-lg">GLOBAL DISCORD SERVER INVITE URL</h3>
        </div>
        <p className="text-white/60 text-sm mb-4">
          Fallback destination when a creator has no per-creator invite set. Paste a permanent invite from
          <span className="font-mono text-neon-cyan"> discord.gg/… </span> or
          <span className="font-mono text-neon-cyan"> discord.com/invite/…</span>
        </p>
        <div className="flex gap-3">
          <input value={invite} onChange={(e) => setInvite(e.target.value)} placeholder="https://discord.gg/YourInviteCode" data-testid="settings-invite-input" className="flex-1 bg-black/60 border border-white/10 px-3 py-3 font-mono text-sm focus:border-neon-cyan outline-none" />
          <button onClick={save} disabled={saving} data-testid="settings-invite-save" className="neon-btn px-5 py-3 inline-flex items-center gap-2 disabled:opacity-50">
            <FloppyDisk size={16} weight="bold" /> {saving ? "SAVING…" : "SAVE"}
          </button>
        </div>
        {invite && (
          <a href={invite} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-xs text-neon-cyan hover:underline font-mono">
            <ArrowSquareOut size={12} /> Test invite in a new tab
          </a>
        )}
      </section>

      <section className="card-tactical p-6">
        <div className="flex items-center gap-2 mb-3">
          <FloppyDisk size={22} color="#E4FF00" weight="fill" />
          <h3 className="display-heading font-bold text-lg">TEBEX FEE %</h3>
        </div>
        <p className="text-white/60 text-sm mb-4">
          Platform/processing fee deducted before creator commission. Creator earnings = (amount − tax) × (1 − fee%) × commission%.
        </p>
        <div className="flex gap-3 max-w-xs">
          <input type="number" min="0" max="100" step="0.1" value={feePct} onChange={(e) => setFeePct(e.target.value)} data-testid="settings-fee-input"
            className="flex-1 bg-black/60 border border-white/10 px-3 py-3 font-mono text-sm focus:border-neon-cyan outline-none" />
          <button onClick={saveFee} disabled={savingFee} data-testid="settings-fee-save" className="neon-btn px-5 py-3 inline-flex items-center gap-2 disabled:opacity-50">
            <FloppyDisk size={16} weight="bold" /> {savingFee ? "…" : "SAVE"}
          </button>
        </div>
      </section>

      <section className="card-tactical p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Robot size={22} color="#E4FF00" weight="fill" />
            <h3 className="display-heading font-bold text-lg">DISCORD BOT STATUS</h3>
          </div>
          <button onClick={refreshInvites} disabled={refreshing || !botStatus?.ready} data-testid="bot-refresh-invites" className="ghost-btn px-3 py-2 text-white kbd-label inline-flex items-center gap-1 disabled:opacity-50">
            <ArrowsClockwise size={13} className={refreshing ? "animate-spin" : ""} /> {refreshing ? "SYNCING…" : "RESNAPSHOT INVITES"}
          </button>
        </div>
        {botStatus ? (
          <div className="grid md:grid-cols-4 gap-3 mb-3">
            <StatBadge label="TOKEN" ok={botStatus.token_configured} />
            <StatBadge label="GUILD" ok={botStatus.guild_configured} />
            <StatBadge label="RUNNING" ok={botStatus.running} accent={botStatus.running ? "#22C55E" : "#FF3B30"} />
            <StatBadge label="CONNECTED" ok={botStatus.ready} accent={botStatus.ready ? "#22C55E" : "#FF3B30"} />
          </div>
        ) : (
          <div className="text-white/40 font-mono text-sm">Loading bot state…</div>
        )}
        {botStatus?.ready && (
          <div className="mt-2 text-xs font-mono text-white/60">
            Watching guild <span className="text-neon-cyan">{botStatus.guild_id}</span> · <span className="text-neon-yellow">{botStatus.invite_count}</span> invites cached · connected at {botStatus.connected_at?.slice(0, 19).replace("T", " ")}
          </div>
        )}
        {botStatus?.last_error && (
          <div className="mt-3 border border-red-500/40 text-red-400 font-mono text-xs px-3 py-2">
            LAST ERROR: {botStatus.last_error}
          </div>
        )}
        {!botStatus?.token_configured && (
          <div className="mt-3 text-xs text-white/50 font-mono">
            Set <span className="text-neon-cyan">DISCORD_BOT_TOKEN</span> and <span className="text-neon-cyan">DISCORD_GUILD_ID</span> in <span className="text-neon-yellow">backend/.env</span>, then <span className="text-neon-yellow">sudo supervisorctl restart backend</span>. Invite the bot with permissions: <span className="text-neon-cyan">Manage Server</span> (required) + <span className="text-neon-cyan">Create Instant Invite</span> (optional, for auto-mint).
          </div>
        )}
      </section>

      <section className="card-tactical p-6 border-l-4 border-l-emerald-500" data-testid="settings-tebex-panel">
        <div className="flex items-center gap-2 mb-2">
          <ShoppingCart size={22} color="#22C55E" weight="fill" />
          <h3 className="display-heading font-bold text-lg">TEBEX (CHECKOUT API)</h3>
        </div>
        <p className="text-white/60 text-sm mb-4">
          Your Tebex project uses the <span className="text-neon-cyan">Checkout API</span>, which doesn&rsquo;t support
          bulk-listing past sales. Live attribution happens via the <span className="text-neon-yellow">webhook</span> — set
          it up in Tebex → Developers → Webhooks pointing at
          <span className="font-mono text-neon-cyan"> /api/webhooks/tebex</span>. CSV upload stays as a fallback for
          historical batches.
        </p>
        <div className="grid md:grid-cols-3 gap-3">
          <StatBadge label="API CREDS" ok={!!tebexStatus?.credentials_configured} />
          <StatBadge label="AUTHENTICATED" ok={!!tebexStatus?.authenticated} accent={tebexStatus?.authenticated ? "#22C55E" : "#FF3B30"} />
          <StatBadge label="WEBHOOK SECRET" ok={!!tebexStatus?.webhook_secret_configured} />
        </div>
        <div className="mt-3 font-mono text-xs text-white/60" data-testid="tebex-store-info">
          {tebexStatus?.project_id
            ? <>Project <span className="text-neon-cyan">{tebexStatus.project_id}</span> · Checkout API</>
            : <span className="text-white/40">No project configured.</span>}
          {tebexStatus?.error && <span className="text-red-400"> · {tebexStatus.error}</span>}
        </div>

        <div className="mt-5 pt-4 border-t border-white/10">
          <div className="kbd-label mb-2">MANUAL VERIFY / BACKFILL A SALE</div>
          <p className="text-white/50 text-xs mb-2 font-mono">
            Paste a <span className="text-neon-cyan">tbx-XXX</span> transaction id from Tebex to fetch, attribute
            (by <span className="text-neon-yellow">discord_id</span> custom var), and record it.
          </p>
          <div className="flex gap-2">
            <input
              value={verifyTxId}
              onChange={(e) => setVerifyTxId(e.target.value)}
              placeholder="tbx-abc123..."
              data-testid="tebex-verify-input"
              className="flex-1 bg-black/60 border border-white/10 px-3 py-2 font-mono text-sm focus:border-neon-cyan outline-none"
            />
            <button
              onClick={verifyTx}
              disabled={verifying || !tebexStatus?.authenticated}
              data-testid="tebex-verify-btn"
              className="neon-btn px-4 py-2 kbd-label whitespace-nowrap disabled:opacity-50"
            >
              {verifying ? "…" : "VERIFY & ATTRIBUTE"}
            </button>
          </div>
        </div>

        <details className="mt-5 pt-4 border-t border-white/10 group">
          <summary className="cursor-pointer kbd-label text-neon-cyan hover:text-white">
            HOW TO FINISH THE WEBHOOK SETUP ▸
          </summary>
          <ol className="mt-3 space-y-2 text-xs font-mono text-white/70 list-decimal list-inside">
            <li>Tebex → Developers → Webhooks → <span className="text-neon-yellow">Add Endpoint</span></li>
            <li>URL: <span className="text-neon-cyan">{typeof window !== "undefined" ? window.location.origin : ""}/api/webhooks/tebex</span></li>
            <li>Subscribe to: <span className="text-neon-yellow">payment.completed</span>, <span className="text-neon-yellow">payment.refunded</span>, <span className="text-neon-yellow">validation.webhook</span></li>
            <li>Copy the generated <span className="text-neon-yellow">Webhook Secret</span> and paste it in backend/.env as <span className="text-neon-cyan">TEBEX_WEBHOOK_SECRET</span></li>
            <li>Configure a required <span className="text-neon-cyan">discord_id</span> custom variable at Tebex → Settings → Custom Variables, attached to every package</li>
          </ol>
        </details>
      </section>

      <section className="card-tactical p-6 border-l-4 border-l-neon-yellow" data-testid="settings-fini-panel">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <GameController size={22} color="#E4FF00" weight="fill" />
            <h3 className="display-heading font-bold text-lg">FIVEM PLAYER SYNC · FINI AC</h3>
          </div>
          <button
            onClick={syncFini}
            disabled={finiSyncing || !finiStatus?.configured}
            data-testid="fini-sync-now"
            className="neon-btn px-4 py-2 kbd-label inline-flex items-center gap-2 disabled:opacity-50"
          >
            <ArrowsClockwise size={14} className={finiSyncing ? "animate-spin" : ""} />
            {finiSyncing ? "SYNCING…" : "SYNC PLAYERS FROM FINI"}
          </button>
        </div>
        <p className="text-white/60 text-sm mb-4">
          Pulls the current online player list from your FiveM server via <span className="text-neon-cyan">Fini AC Apollo</span>.
          Every online player with a Discord ID gets marked as an active player — matching them against your attributed users
          closes the loop from &ldquo;joined Discord&rdquo; to &ldquo;actually playing&rdquo;. Poll every few minutes to catch everyone who plays.
        </p>
        <div className="grid md:grid-cols-3 gap-3">
          <StatBadge label="KEYS" ok={!!finiStatus?.configured} />
          <StatBadge label="SERVER IP" ok={!!finiStatus?.server_ip} accent="#00F0FF" />
          <StatBadge label="SERVER ONLINE" ok={!!finiStatus?.server?.online} accent={finiStatus?.server?.online ? "#22C55E" : "#FF3B30"} />
        </div>
        {finiStatus?.server_ip && (
          <div className="mt-3 font-mono text-xs text-white/60">
            Polling <span className="text-neon-cyan">{finiStatus.server_ip}</span>
            {finiStatus?.server?.max_players ? ` · slots ${finiStatus.server.max_players}` : ""}
          </div>
        )}
        {finiStatus?.server_error && (
          <div className="mt-3 border border-red-500/40 text-red-400 font-mono text-xs px-3 py-2">
            {finiStatus.server_error}
          </div>
        )}
        {finiStatus?.error && (
          <div className="mt-3 border border-red-500/40 text-red-400 font-mono text-xs px-3 py-2">
            {finiStatus.error}
          </div>
        )}
        {finiStatus?.info?.permissions && (
          <div className="mt-2 font-mono text-xs text-white/50">
            Key permissions: <span className="text-neon-cyan">{finiStatus.info.permissions}</span>
          </div>
        )}
      </section>


      <section className="card-tactical p-6">
        <h3 className="display-heading font-bold text-lg mb-2">INTEGRATION STATUS</h3>
        <Row label="Discord OAuth (visitor identify)" ok={health.discord_oauth_configured} hint="Optional. Set DISCORD_CLIENT_ID + DISCORD_CLIENT_SECRET in backend/.env" />
        <Row label="Discord Bot (invite tracking + join/leave)" ok={botStatus?.ready} hint="Recommended. DISCORD_BOT_TOKEN + DISCORD_GUILD_ID. Bot needs Server Members Intent + Manage Server." />
        <Row label="Discord Invite URL (global fallback)" ok={!!invite} hint="Above — where visitors without a creator-override go" />
        <Row label="Tebex Webhook (instant sync)" ok={health.tebex_webhook_configured} hint="Set TEBEX_WEBHOOK_SECRET + add endpoint /api/webhooks/tebex in Tebex dashboard." />
        <Row label="Tebex Store API (polling reconcile)" ok={health.tebex_api_configured} hint="Set TEBEX_SERVER_SECRET for reconciliation polling." />
        <Row label="Fini AC (FiveM active-player match)" ok={health.fini_configured} hint="Set FINI_ACCESS_KEY + FINI_SECRET_KEY. Requires USERS.SEARCH permission." />
        <Row label="FiveM Lua resource (optional)" ok={health.fivem_configured} hint="Set FIVEM_API_KEY, deploy Lua resource at /app/fivem_resource/ — only if you want live connect events." />
      </section>
    </div>
  );
}

function StatBadge({ label, ok, accent }) {
  const color = accent || (ok ? "#22C55E" : "#71717A");
  return (
    <div className="border border-white/10 p-3">
      <div className="kbd-label">{label}</div>
      <div className="mt-1 font-mono text-sm" style={{ color }}>
        {ok ? "YES" : "NO"}
      </div>
    </div>
  );
}

